/**
 * 主題一鍵生成 dialog（Mode B）
 *
 * 對應「人類因懶惰而創造，AI 一鍵創造是系統存在的意義」核心哲學。
 * 使用者貼一段策略描述 → AI 一次拆出完整 SubjectDraft → 預覽勾選 → 一鍵建立
 *   主題（status=planning, created_by_ai=TRUE）
 *   + N 個對象（含誘因，created_by_ai=TRUE）
 *   + N 個議題（status=planning, created_by_ai=TRUE, 自動掛主題 is_primary=TRUE）
 */

import React, { useState } from 'react';
import {
  Box, Stack, Button, Chip, IconButton, Tooltip, Typography,
  Paper, CircularProgress, Checkbox, Divider, Alert, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon, ArrowForward as NextIcon,
  Close as CloseIcon, Person as PersonIcon, Topic as TopicIcon,
} from '@mui/icons-material';

import { useMessage } from '../../contexts/MessageContext';
import { logCreate } from '../../utils/userLogHelper';
import {
  SubjectDraft, AudienceType,
} from '../../types/research';
import {
  draftSubjectFromDescription,
  createSubject, createAudience, createTopic, addTopicToSubject,
} from '../../services/researchService';

const FUNC_CODE = 'ee_research_subjects';

const SAMPLE_TEXT = `例：我們公司想推一個應用系統資安平台，目標是說服中央級政府機關與大型 SI 採購；半年內要拿下 5 家客戶。對手是國際品牌但價格貴，我們強調本土支援與合規對齊。`;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (subjectId: number) => void;
  // Mode A 結束時把 ready=true 的 draft 丟給這個 dialog 直接進 preview
  initialDraft?: SubjectDraft | null;
}

const SubjectOneClickDialog: React.FC<Props> = ({ open, onClose, onCreated, initialDraft }) => {
  const { showSuccess, showApiError } = useMessage();

  const [stage, setStage] = useState<'input' | 'preview'>('input');
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState<SubjectDraft | null>(null);
  const [audiencePicks, setAudiencePicks] = useState<Set<number>>(new Set());
  const [topicPicks, setTopicPicks] = useState<Set<number>>(new Set());
  const [adopting, setAdopting] = useState(false);

  // Mode A 接手：開啟時若帶 initialDraft，跳過 input 直接進 preview
  React.useEffect(() => {
    if (open && initialDraft) {
      // 防呆：suggested_audiences / suggested_topics 必為陣列
      const safe: SubjectDraft = {
        ...initialDraft,
        suggested_audiences: initialDraft.suggested_audiences ?? [],
        suggested_topics: initialDraft.suggested_topics ?? [],
      };
      setDraft(safe);
      setAudiencePicks(new Set(safe.suggested_audiences.map((_, i) => i)));
      setTopicPicks(new Set(safe.suggested_topics.map((_, i) => i)));
      setStage('preview');
    }
  }, [open, initialDraft]);

  const reset = () => {
    setStage('input');
    setDescription('');
    setDraft(null);
    setAudiencePicks(new Set());
    setTopicPicks(new Set());
  };

  const handleClose = () => {
    if (generating || adopting) return;
    reset();
    onClose();
  };

  const handleGenerate = async () => {
    const desc = description.trim();
    if (desc.length < 10) {
      showApiError({ response: { data: { detail: '請寫至少 10 字的策略描述' } } });
      return;
    }
    setGenerating(true);
    try {
      const d = await draftSubjectFromDescription(desc);
      if (!d || !d.subject_name) {
        showApiError({ response: { data: { detail: 'AI 沒拆出有效草稿，再試一次或補充描述' } } });
        return;
      }
      // 編輯草稿：subject_name / description 可改
      setDraft(d);
      // 預設全勾選對象 + 議題
      setAudiencePicks(new Set(d.suggested_audiences.map((_, i) => i)));
      setTopicPicks(new Set(d.suggested_topics.map((_, i) => i)));
      setStage('preview');
    } catch (err: any) {
      showApiError(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleAdopt = async () => {
    if (!draft) return;
    setAdopting(true);

    let subjectId: number | null = null;
    let aOk = 0, aFail = 0;
    let tOk = 0, tFail = 0;
    const audErrors: string[] = [];

    // 把可能無效的 code 補成 fallback slug（Mode A LLM 對話多輪後常漏欄/含中文）
    const slugify = (s: string, prefix: string) => {
      const safe = (s || '').toLowerCase()
        .replace(/[^\w\s-]/g, '')         // 去中文、特殊符號
        .trim()
        .replace(/\s+/g, '_')
        .substring(0, 12);
      return safe.length >= 3 ? safe : `${prefix}_${Date.now().toString(36).slice(-6)}`;
    };

    // audience_type 必須是固定 enum；LLM 可能填中文或非法值，sanitize 成 'other' fallback
    const VALID_TYPES: AudienceType[] = ['government', 'investor', 'technical', 'executive', 'public', 'other'];
    const sanitizeType = (raw: any): AudienceType => {
      if (typeof raw === 'string' && VALID_TYPES.includes(raw as AudienceType)) {
        return raw as AudienceType;
      }
      return 'other';
    };

    const clamp01 = (v: any): number => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return 0.5;
      return Math.max(0, Math.min(1, n));
    };

    const truncStr = (s: any, max: number): string => {
      const t = (typeof s === 'string' ? s : '').trim();
      return t.length > max ? t.substring(0, max) : t;
    };

    const safeSubjectCode = (draft.subject_code && /^[a-z0-9_]+$/i.test(draft.subject_code))
      ? draft.subject_code
      : slugify(draft.subject_name, 'subj');

    // eslint-disable-next-line no-console
    console.log('[handleAdopt] draft snapshot:', JSON.stringify(draft, null, 2));
    // eslint-disable-next-line no-console
    console.log('[handleAdopt] safeSubjectCode:', safeSubjectCode);

    try {
      // 1. 建主題
      const subject = await createSubject({
        subject_code: safeSubjectCode,
        subject_name: draft.subject_name || '未命名主題',
        description: draft.description,
        status: 'active',
        is_active: true,
      } as any);
      subjectId = subject.id;
      logCreate(FUNC_CODE, { ...subject, created_by_ai: true });

      // 2. 建對象（user 勾選的）
      for (let i = 0; i < draft.suggested_audiences.length; i++) {
        if (!audiencePicks.has(i)) continue;
        const a = draft.suggested_audiences[i];
        try {
          await createAudience(subjectId, {
            audience_name: truncStr(a.audience_name, 200) || '未命名對象',
            audience_type: sanitizeType(a.audience_type),
            persona: truncStr(a.persona, 2000) || undefined,
            incentives: (a.suggested_incentives ?? [])
              .filter(p => p && (p.point ?? '').trim())   // 空 point 直接 skip
              .map(p => ({
                point: truncStr(p.point, 300),
                weight: clamp01(p.weight),
                evidence: truncStr(p.evidence_hint, 500) || undefined,
              })),
          });
          aOk++;
        } catch (err: any) {
          aFail++;
          const detail = err?.response?.data?.detail;
          const reason = typeof detail === 'string' ? detail
            : detail?.code ? `${detail.code} ${JSON.stringify(detail.params || {})}`
            : err?.message || 'unknown';
          audErrors.push(`「${a.audience_name || '#' + (i + 1)}」：${reason}`);
          // eslint-disable-next-line no-console
          console.warn(`[OneClickDialog] audience create failed:`, a, err);
        }
      }

      // 3. 建議題 + 掛主題（user 勾選的）
      const topicErrors: string[] = [];
      for (let i = 0; i < draft.suggested_topics.length; i++) {
        if (!topicPicks.has(i)) continue;
        const t = draft.suggested_topics[i];
        const tName = (t.topic_name || '').trim();
        if (!tName) {
          tFail++;
          topicErrors.push(`#${i + 1}：缺 topic_name（AI 漏欄）`);
          continue;
        }
        // topic_code 不合法時 fallback slug（Mode A 多輪後常含中文 / 缺欄）
        const safeTopicCode = (t.topic_code && /^[a-z0-9_]+$/i.test(t.topic_code))
          ? t.topic_code
          : slugify(tName, 'topic');
        try {
          const created = await createTopic({
            topic_code: safeTopicCode,
            topic_name: tName,
            description: t.description ?? undefined,
            search_keywords: t.keywords ?? [],
            tag_dimensions: ['地區', '面向', '事件日期'],
            is_active: true,
          } as any);
          await addTopicToSubject(subjectId, created.id, true);
          tOk++;
        } catch (err: any) {
          tFail++;
          const detail = err?.response?.data?.detail;
          const reason = typeof detail === 'string' ? detail
            : detail?.code ? `${detail.code} ${JSON.stringify(detail.params || {})}`
            : err?.message || 'unknown';
          topicErrors.push(`「${tName}」(${safeTopicCode})：${reason}`);
          // eslint-disable-next-line no-console
          console.warn(`[OneClickDialog] topic create failed:`, t, err);
        }
      }

      const summary = `主題「${draft.subject_name}」已建立 — 對象 ${aOk}${aFail ? `(失 ${aFail})` : ''} / 議題 ${tOk}${tFail ? `(失 ${tFail})` : ''}`;
      showSuccess('SYS020001', { name: summary });
      if (audErrors.length > 0 || topicErrors.length > 0) {
        const parts: string[] = [];
        if (audErrors.length > 0) parts.push(`${aFail} 個對象失敗：\n${audErrors.join('\n')}`);
        if (topicErrors.length > 0) parts.push(`${tFail} 個議題失敗：\n${topicErrors.join('\n')}`);
        // eslint-disable-next-line no-alert
        window.alert(`${parts.join('\n\n')}\n\n（成功的部分仍生效；可到主題詳情頁手動補）`);
      }
      reset();
      onCreated(subjectId);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setAdopting(false);
    }
  };

  // ─── render ───
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MagicIcon color="primary" />
          ✨ AI 一鍵生成主題
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={handleClose}
                      disabled={generating || adopting}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {stage === 'input'
            ? '貼一段策略描述，AI 一次拆出主題 + 對象 + 誘因 + 議題草稿'
            : '預覽 AI 草稿；勾選要採納的部分，按「建立此主題」一次完成'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {stage === 'input' ? (
          <Stack spacing={2}>
            <TextField
              label="策略描述" required multiline rows={10}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={SAMPLE_TEXT}
              helperText={`${description.length} / 5000 字（至少 10 字）`}
              inputProps={{ maxLength: 5000 }}
              autoFocus
            />
            <Alert severity="info">
              <strong>建議內容：</strong>策略目標 / 對象（誰要被說服）/ 期限 / 強項與痛點 / 競爭背景。
              寫得越具體，AI 拆出的草稿越貼近實際需要。
            </Alert>
          </Stack>
        ) : draft ? (
          <Stack spacing={2}>
            {/* 主題 part */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" color="primary">主題</Typography>
              <TextField
                fullWidth size="small" label="主題代碼"
                value={draft.subject_code}
                onChange={e => setDraft({ ...draft, subject_code: e.target.value })}
                helperText="英數底線；建立後不可改" sx={{ mt: 1 }}
              />
              <TextField
                fullWidth size="small" label="主題名稱"
                value={draft.subject_name}
                onChange={e => setDraft({ ...draft, subject_name: e.target.value })}
                sx={{ mt: 1.5 }}
              />
              <TextField
                fullWidth size="small" label="描述"
                multiline rows={2}
                value={draft.description}
                onChange={e => setDraft({ ...draft, description: e.target.value })}
                sx={{ mt: 1.5 }}
              />
            </Paper>

            {/* 對象 part */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="overline" color="primary">
                  對象與誘因（{audiencePicks.size}/{draft.suggested_audiences.length}）
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={() =>
                  setAudiencePicks(new Set(draft.suggested_audiences.map((_, i) => i)))
                }>全選</Button>
                <Button size="small" onClick={() => setAudiencePicks(new Set())}>全不選</Button>
              </Stack>
              {draft.suggested_audiences.length === 0 ? (
                <Typography variant="caption" color="text.disabled">AI 沒拆出對象</Typography>
              ) : (
                <Stack spacing={1}>
                  {draft.suggested_audiences.map((a, i) => {
                    const checked = audiencePicks.has(i);
                    return (
                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start"
                             sx={{ p: 1, bgcolor: checked ? 'action.selected' : 'inherit',
                                   borderRadius: 1 }}>
                        <Checkbox size="small" checked={checked}
                                  onChange={() => {
                                    const next = new Set(audiencePicks);
                                    if (next.has(i)) next.delete(i); else next.add(i);
                                    setAudiencePicks(next);
                                  }} />
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2">{a.audience_name}</Typography>
                            {a.audience_type && (
                              <Chip size="small" label={a.audience_type} variant="outlined" />
                            )}
                          </Stack>
                          {a.persona && (
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}>
                              {a.persona}
                            </Typography>
                          )}
                          {a.suggested_incentives.length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap"
                                   useFlexGap sx={{ mt: 0.5 }}>
                              {a.suggested_incentives.map((p, j) => (
                                <Tooltip key={j} title={p.evidence_hint || ''}>
                                  <Chip size="small" label={p.point}
                                        sx={{ opacity: 0.6 + p.weight * 0.4 }}
                                        variant="outlined" />
                                </Tooltip>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* 議題 part */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="overline" color="primary">
                  議題（{topicPicks.size}/{draft.suggested_topics.length}）
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={() =>
                  setTopicPicks(new Set(draft.suggested_topics.map((_, i) => i)))
                }>全選</Button>
                <Button size="small" onClick={() => setTopicPicks(new Set())}>全不選</Button>
              </Stack>
              {draft.suggested_topics.length === 0 ? (
                <Typography variant="caption" color="text.disabled">AI 沒拆出議題</Typography>
              ) : (
                <Stack spacing={1}>
                  {draft.suggested_topics.map((t, i) => {
                    const checked = topicPicks.has(i);
                    return (
                      <Stack key={i} direction="row" spacing={1} alignItems="flex-start"
                             sx={{ p: 1, bgcolor: checked ? 'action.selected' : 'inherit',
                                   borderRadius: 1 }}>
                        <Checkbox size="small" checked={checked}
                                  onChange={() => {
                                    const next = new Set(topicPicks);
                                    if (next.has(i)) next.delete(i); else next.add(i);
                                    setTopicPicks(next);
                                  }} />
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1}>
                            <TopicIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2">{t.topic_name}</Typography>
                            <Typography variant="caption" color="text.disabled"
                                        sx={{ fontFamily: 'monospace' }}>
                              {t.topic_code}
                            </Typography>
                          </Stack>
                          {t.description && (
                            <Typography variant="caption" color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}>
                              {t.description}
                            </Typography>
                          )}
                          {(t.keywords ?? []).length > 0 && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap"
                                   useFlexGap sx={{ mt: 0.5 }}>
                              {(t.keywords ?? []).map((k, j) => (
                                <Chip key={j} size="small" label={k} variant="outlined" />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            <Alert severity="info">
              建立後主題狀態為「<strong>規劃中</strong>」，需到主題詳情頁手動「啟動主題」才會解鎖議題層的蒐集。
            </Alert>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        {stage === 'input' ? (
          <>
            <Button onClick={handleClose} disabled={generating}>取消</Button>
            <Button variant="contained" onClick={handleGenerate}
                    disabled={generating || description.trim().length < 10}
                    startIcon={generating ? <CircularProgress size={14} /> : <MagicIcon />}>
              {generating ? 'AI 拆解中…' : '✨ 一鍵生成草稿'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStage('input')} disabled={adopting}>
              ← 重寫描述
            </Button>
            <Button onClick={handleClose} disabled={adopting}>取消</Button>
            <Button variant="contained" onClick={handleAdopt}
                    disabled={adopting || !draft}
                    endIcon={adopting ? <CircularProgress size={14} /> : <NextIcon />}>
              {adopting ? '建立中…' : `建立此主題（含 ${audiencePicks.size} 對象 / ${topicPicks.size} 議題）`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubjectOneClickDialog;
